import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Solutions', href: '/#solutions' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === '/';
  const isDark = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? 'bg-stone-50/85 backdrop-blur-md border-b border-stone-200/50 shadow-sm shadow-stone-200/20'
          : 'bg-transparent'
      }`}
    >
      <div className="section-padding flex items-center justify-between h-20">
        {/* Logo */}
        <Link
          to="/"
          className={`font-serif text-xl tracking-wide transition-colors duration-300 ${
            isDark ? 'text-stone-100' : 'text-stone-800'
          }`}
        >
          7th <span className="font-semibold text-bronze-500">Vision</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((link) =>
            link.href.startsWith('/#') ? (
              <a
                key={link.href}
                href={link.href}
                className={`text-xs tracking-widest uppercase font-sans font-medium transition-colors duration-300 ${
                  isDark
                    ? 'text-stone-300 hover:text-stone-100'
                    : 'text-stone-500 hover:text-stone-800'
                }`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                className={`text-xs tracking-widest uppercase font-sans font-medium transition-colors duration-300 ${
                  isDark
                    ? 'text-stone-300 hover:text-stone-100'
                    : 'text-stone-500 hover:text-stone-800'
                }`}
              >
                {link.label}
              </Link>
            )
          )}
          <Link
            to="/contact"
            className={`text-xs tracking-widest uppercase font-sans font-medium py-3 px-6 border transition-all duration-300 ${
              isDark
                ? 'border-stone-300 text-stone-100 hover:bg-stone-100 hover:text-stone-800'
                : 'border-stone-800 text-stone-800 hover:bg-stone-800 hover:text-stone-50'
            }`}
          >
            Schedule a Conversation
          </Link>
        </nav>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`md:hidden p-2 transition-colors duration-300 ${
            isDark ? 'text-stone-100' : 'text-stone-800'
          }`}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden bg-stone-50 border-b border-stone-200 overflow-hidden transition-all duration-300 ${
          menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="section-padding py-6 flex flex-col gap-6">
          {NAV_LINKS.map((link) =>
            link.href.startsWith('/#') ? (
              <a
                key={link.href}
                href={link.href}
                className="text-xs tracking-widest uppercase font-sans font-medium text-stone-500 hover:text-stone-800 transition-colors duration-300"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                className="text-xs tracking-widest uppercase font-sans font-medium text-stone-500 hover:text-stone-800 transition-colors duration-300"
              >
                {link.label}
              </Link>
            )
          )}
          <Link
            to="/contact"
            className="btn-outline self-start"
          >
            Schedule a Conversation
          </Link>
        </div>
      </div>
    </header>
  );
}
